import { Stream } from "@cloudflare/stream-react";
import { useEffect, useRef, useState } from "react";
import { fetchGetVideo, fetchUpdateUserVideoStatistics } from "../FolderApi";
import { useDispatch, useSelector } from "react-redux";
import { showError } from "../../../features/Error/errorSlice";
import { selectTranslations } from "../../../services/i18n/i18nSlice";

import debounce from "lodash.debounce";
import { Spinner, SplitButton } from "react-bootstrap";

import Skeleton from "react-loading-skeleton";
import 'react-loading-skeleton/dist/skeleton.css'
import { toast } from "react-toastify";
import { TENANT } from "../../../config/constants";
import { GetFromLocalStorage, SaveToLocalStorage } from "../../../utils/utils";

export default function VideoPlayerInline({videoId=null, onEnd=null, poster, folderData=null}) {

    const [videoData, setVideoData] = useState(null)
    const [videoReady, setVideoReady] = useState(false)

    const dispatch = useDispatch()

    const videoRef = useRef()
    const maxWatchedTimeRef = useRef(0) // Traccia la posizione massima raggiunta
    const isSeekingBackRef = useRef(false) // Flag per evitare loop infiniti
    const lastTimeBeforeSeekRef = useRef(0) // Posizione prima del seek
    const isSeekingRef = useRef(false) // Flag per sapere se è in corso un seek

    const t = useSelector(selectTranslations)
    
    const loadVideoData = async () => {
        const response = await fetchGetVideo({video_id: videoId})
        if(response.success){
            setVideoData(response.data)
        } else {
            dispatch(showError({
                title: t.folder.error.loading_video,
                text: t.folder.error.loading_video_text
            }))  
        }
    }

    const handleTrackVideoEnd = async (e) => {
        const response = await fetchUpdateUserVideoStatistics({
            video_id: videoId,
            completed: videoData?.completed || videoRef.current?.ended
        })
        if(!response.success)
            console.error(response.error);
        
        // Controlla se deve chiamare onEnd (non ancora mostrato modal per questo video)
        const storageKey = `${TENANT}_video_completed_modal_shown`;
        const shownVideos = GetFromLocalStorage(storageKey) || {};
        
        if (!shownVideos[videoId] && onEnd) {
            // Salva subito che il modal è stato considerato per questo video
            shownVideos[videoId] = true;
            SaveToLocalStorage(storageKey, shownVideos);
            onEnd();
        }
        
        const response_video = await fetchGetVideo({video_id: videoId})
        if(response_video.success){
            setVideoData({
                ...videoData,
                completed: response_video.data.completed
            })
        } 
    }

    const _handleTrackVideoProgress = async () => {
        const response = await fetchUpdateUserVideoStatistics({
            video_id: videoId,
            completed: videoData?.completed || videoRef.current?.ended,
            seconds_elapsed: videoRef.current?.currentTime || 0
        })
        if(!response.success)
            console.error(response.error);
    }

    const handleTrackVideoProgress = debounce(_handleTrackVideoProgress, 300)

    // Verifica se la cartella è di tipo learning_path
    const isLearningPath = () => {
        return folderData?.type === 'learning_path' || 
               folderData?.parent_folder_details?.type === 'learning_path';
    };

    // Cattura la posizione PRIMA che inizi il seek
    const handleSeeking = (e) => {
        if (!isSeekingRef.current) {
            lastTimeBeforeSeekRef.current = videoRef.current?.currentTime || 0;
            isSeekingRef.current = true;
        }
    };

    // Previene il seek in avanti se il video non è completato E la cartella è learning_path
    const handleSeeked = (e) => {
        isSeekingRef.current = false;

        // Ignora se stiamo riportando indietro il video noi stessi
        if (isSeekingBackRef.current) {
            isSeekingBackRef.current = false;
            lastTimeBeforeSeekRef.current = videoRef.current?.currentTime || 0;
            return;
        }

        const currentTime = videoRef.current?.currentTime || 0;
        
        // Se il video è già completato, permetti qualsiasi seek
        if (videoData?.completed) {
            lastTimeBeforeSeekRef.current = currentTime;
            return;
        }

        // Blocca il seek SOLO se la cartella è di tipo learning_path
        if (!isLearningPath()) {
            lastTimeBeforeSeekRef.current = currentTime;
            return;
        }

        // Controlla se l'utente ha saltato AVANTI oltre la posizione massima raggiunta
        if (currentTime > maxWatchedTimeRef.current + 1) { // tolleranza di 1 secondo
            isSeekingBackRef.current = true; // Imposta flag per evitare loop
            videoRef.current.currentTime = maxWatchedTimeRef.current;
            // Usa toast se disponibile, altrimenti silenzioso
            toast.info(t?.folder?.video_seek_blocked || "Completa prima questa parte del video")
        } else {
            // Aggiorna l'ultima posizione valida
            lastTimeBeforeSeekRef.current = currentTime;
        }
    };

    // Aggiorna la posizione massima raggiunta durante la riproduzione normale
    const handleTimeUpdateWithSeekControl = (e) => {
        const currentTime = videoRef.current?.currentTime || 0;
        
        // Aggiorna il massimo SOLO se NON siamo in un seek e stiamo procedendo linearmente
        if (!isSeekingRef.current && currentTime > maxWatchedTimeRef.current) {
            maxWatchedTimeRef.current = currentTime;
        }

        // Chiama il tracking normale
        handleTrackVideoProgress(e);
    };

    useEffect(()=>{
        if(videoId){
            setVideoReady(false)
            loadVideoData()
        }
    },[videoId])

    // Inizializza maxWatchedTime quando carichi i dati del video
    useEffect(() => {
        if (videoData?.seconds_elapsed) {
            maxWatchedTimeRef.current = videoData.seconds_elapsed;
        }
        // Se il video è completato, permetti di seekare ovunque
        if (videoData?.completed && videoRef.current?.duration) {
            maxWatchedTimeRef.current = videoRef.current.duration;
        }
    }, [videoData]);

    return(
        <section className="video_player_inline">
            
                {
                    videoData ?
                        <>
                            <Stream 
                                controls={true} 
                                streamRef={videoRef}
                                poster={videoData?.poster_image_url}
                                src={videoData?.uid} 
                                onEnded={handleTrackVideoEnd}
                                onTimeUpdate={handleTimeUpdateWithSeekControl}
                                onSeeking={handleSeeking}
                                onSeeked={handleSeeked}
                                onCanPlay={()=>setVideoReady(true)}
                            />
                            {
                                !videoReady &&
                                    <Spinner />
                            }
                        </>
                     :   
                     <>
                        <img src={poster} className='mt-1 video_preview' alt=""/>
                     </>
                     
                }
        </section>
    )
}